import type { ReactNode } from 'react'
import { ApiError } from '../lib/api'
import { money, signedMoney } from '../lib/format'

/** The small pieces every screen needs. Nothing here holds server state. */

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl bg-ink-soft/70 ring-1 ring-ink-line/60 ${className}`}>
      {children}
    </div>
  )
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  type = 'button',
  disabled = false,
  className = '',
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  type?: 'button' | 'submit'
  disabled?: boolean
  className?: string
}) {
  const styles = {
    primary: 'bg-accent text-ink font-semibold',
    secondary: 'bg-ink-soft text-paper ring-1 ring-ink-line',
    danger: 'bg-owing/15 text-owing ring-1 ring-owing/40',
    ghost: 'text-muted',
  }[variant]
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-xl px-4 py-3 transition active:scale-[0.98] disabled:opacity-40 ${styles} ${className}`}
    >
      {children}
    </button>
  )
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string
  children: ReactNode
  hint?: string
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-muted">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  )
}

export const inputClass =
  'w-full rounded-xl bg-ink px-3 py-3 text-paper ring-1 ring-ink-line focus:ring-accent focus:outline-none'

/** An amount with the sign convention applied: green means the viewer is owed. */
export function Balance({
  netMinor,
  currency,
  className = '',
}: {
  netMinor: number
  currency: string
  className?: string
}) {
  const tone = netMinor === 0 ? 'text-muted' : netMinor > 0 ? 'text-owed' : 'text-owing'
  return (
    <span className={`tnum ${tone} ${className}`}>
      {netMinor === 0 ? money(0, currency) : signedMoney(netMinor, currency)}
    </span>
  )
}

/** The plain-language version of a balance, since a sign alone is ambiguous. */
export function balanceSentence(netMinor: number, currency: string, theirName: string): string {
  if (netMinor === 0) return '結清了'
  return netMinor > 0
    ? `${theirName} 欠你 ${money(netMinor, currency)}`
    : `你欠 ${theirName} ${money(-netMinor, currency)}`
}

export function Spinner({ label = '載入中' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-muted" role="status">
      <span className="size-4 animate-spin rounded-full border-2 border-muted border-t-transparent" />
      {label}
    </div>
  )
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="py-10 text-center">
      <p className="text-paper">{title}</p>
      {hint ? <p className="mt-1 text-sm text-muted">{hint}</p> : null}
    </div>
  )
}

/**
 * Error display.
 *
 * A 409 is not a failure the user caused, so it gets its own wording and a retry
 * affordance: someone else changed the same thing first, and the fix is to look
 * at the current state (design SS5.6).
 */
export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null
  const { title, body } = describe(error)
  return (
    <div
      role="alert"
      className="rounded-xl bg-owing/10 p-3 text-sm text-owing ring-1 ring-owing/30"
    >
      <p className="font-medium">{title}</p>
      <p className="mt-0.5 text-owing/80">{body}</p>
      {onRetry ? (
        <button type="button" onClick={onRetry} className="mt-2 underline">
          重新載入
        </button>
      ) : null}
    </div>
  )
}

function describe(error: unknown): { title: string; body: string } {
  if (error instanceof ApiError) {
    if (error.isSettledImmutable) {
      return {
        title: '已結清，無法修改',
        body: '這筆分帳已經結清。結清後的金額不會被更動 — 如需調整，請新增一筆分帳。',
      }
    }
    if (error.isConflict) {
      return {
        title: '有人先改了這筆資料',
        body: `${error.message} 重新載入以看到最新的內容，再試一次。`,
      }
    }
    return { title: '操作未完成', body: error.message }
  }
  if (error instanceof Error) return { title: '操作未完成', body: error.message }
  return { title: '操作未完成', body: String(error) }
}
